import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import {
	AbstractControl,
	FormBuilder,
	FormControl,
	FormGroup,
	Validators,
} from '@angular/forms';
import { convertToBytes } from '@core/util/file';
import { ICON } from '@core/util/icon';
import { Address } from '@models/address';
import { UserProfile } from '@models/userProfile';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { AuthState } from '@state/auth';
import { AuthDispatch } from '@state/auth/auth.dispatch';
import { LandscapePositions } from '@state/auth/auth.models';
import { UserProfileState } from '@state/user-profile';
import { Observable } from 'rxjs';
import { Select } from '@ngxs/store';
import 'firebase/auth';
import { BusinessSignUpRequestModel } from '@models/businessSignUpRequests';
import { BusinessState } from '@state/business';
import { Business } from '@models/businesses';
import { MatterDocumentFlows } from '@models/documents';

const BUSINESS_INFORMATION = {
	NAME: 'businessName',
	BUSINESS_ADDRESS: 'businessAddress',
	LETTERHEAD_FILE: 'letterheadFile',
} as const;

export interface BusinessInformationFormData {
	[BUSINESS_INFORMATION.NAME]: string;
	[BUSINESS_INFORMATION.BUSINESS_ADDRESS]: Address;
	[BUSINESS_INFORMATION.LETTERHEAD_FILE]?: File;
}

@UntilDestroy()
@Component({
	selector: 'app-create-user-business',
	templateUrl: './create-user-business.component.html',
	styleUrls: ['./create-user-business.component.scss'],
	standalone: false,
})
export class CreateUserBusinessComponent implements OnInit {
	@ViewSelectSnapshot(AuthState.getUserId) userId: string;
	@ViewSelectSnapshot(UserProfileState.getProfile) userProfile: UserProfile;
	@ViewSelectSnapshot(BusinessState.getBusiness) business: Business;
	@Select(AuthState.getSignUpRequest)
	signUpRequest$: Observable<BusinessSignUpRequestModel>;

	public MatterDocumentFlows = MatterDocumentFlows;

	@Input() loading = false;
	@Input() formData: BusinessInformationFormData;

	@Output() onSubmit: EventEmitter<BusinessInformationFormData> =
		new EventEmitter();
	@Output() onBack: EventEmitter<void> = new EventEmitter();

	public authForm: FormGroup;
	public icon = ICON;
	public landscapePositions = LandscapePositions;
	public BUSINESS_INFORMATION = BUSINESS_INFORMATION;

	public letterHeadFile: File;
	public date = new Date();
	public fileSize: number;
	public showError = false;
	public maxFileSize = convertToBytes(2, 'MB');

	constructor(
		protected auth: AngularFireAuth,
		private formBuilder: FormBuilder,
		private authDispatch: AuthDispatch,
	) {}

	ngOnInit(): void {
		this.authForm = this.createNewAuthForm();

		if (this.formData) {
			this.authForm.patchValue(this.formData);
		} else {
			this.signUpRequest$.pipe(untilDestroyed(this)).subscribe((request) => {
				if (request?.companyName) {
					this.companyName.setValue(request.companyName);
				}
				if (request?.address) {
					this.form[BUSINESS_INFORMATION.BUSINESS_ADDRESS].setValue(
						request.address,
					);
				}
			});
		}
	}

	private createNewAuthForm(): FormGroup {
		return this.formBuilder.group({
			[BUSINESS_INFORMATION.NAME]: new FormControl('', [Validators.required]),
			[BUSINESS_INFORMATION.BUSINESS_ADDRESS]: new FormControl('', [
				Validators.required,
			]),
			[BUSINESS_INFORMATION.LETTERHEAD_FILE]: new FormControl(null),
		});
	}

	private setFile(file: File) {
		this.form[BUSINESS_INFORMATION.LETTERHEAD_FILE].setValue(file);
	}

	get companyName(): AbstractControl {
		return this.form[BUSINESS_INFORMATION.NAME];
	}

	get address(): AbstractControl {
		return this.form[BUSINESS_INFORMATION.BUSINESS_ADDRESS];
	}

	get file(): AbstractControl {
		return this.form[BUSINESS_INFORMATION.LETTERHEAD_FILE];
	}

	get form(): { [key: string]: AbstractControl } {
		return this.authForm.controls;
	}

	public updateLandscapePosition(position: number): void {
		this.authDispatch.SetLandscapePosition(position);
	}

	public onSubmitForm(): void {
		if (this.authForm.valid) {
			this.onSubmit.emit(this.authForm.value as BusinessInformationFormData);
		} else {
			this.authForm.markAllAsTouched();
		}
	}

	public _onBack = (): void => {
		this.onBack.emit();
	};

	removeLetterHeadFile() {
		this.setFile(null);
		this.letterHeadFile = null;
	}

	onLetterHeadFileSelected(file: File) {
		this.setFile(file);
		this.letterHeadFile = file;

		// file size must be less than 2MB
		if (file.size > this.maxFileSize) {
			this.setFile(null);
			this.letterHeadFile = null;
			this.showError = true;
		} else {
			this.fileSize = file.size;
			this.showError = false;
		}
	}
}
